import React from 'react'
import SingleQuestion from './SingleQuestion'
import { FieldArray } from 'formik'
import { QuestionGroup } from './QuestionGroup'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import RepeatableQuestionGroup from './RepeatableQuestionGroup'
import AddressFieldsAutoComplete from './addressAutoComplete'
import { getAutocompleteFillAddressValues } from '@mortgage-pos/ui/utils/questionHelpers'
import { useShouldShowQuestion } from '@mortgage-pos/ui/utils/visibleIf'

import {
  QuestionType,
  QuestionCreateAccount,
  QuestionGroup as QuestionGroupType,
} from '@mortgage-pos/types'

export default function FormQuestion({ values, question }) {
  const { mergedAnswers } = useStoreState((state) => state.answers)
  const prebuiltValidations = useStoreState(
    (state) => state.visibleIfConditions
  )

  const questionName: string = question.questionName.name

  const allAnswers = { ...mergedAnswers, ...values }
  const shouldShowQuestion = useShouldShowQuestion(
    allAnswers,
    question,
    prebuiltValidations
  )

  const autoCompleteFillAddressValues =
    getAutocompleteFillAddressValues(mergedAnswers)

  const { fieldInputted } = useStoreActions((state) => state.tracking)

  if (!shouldShowQuestion) {
    return null
  }

  if (
    question.component === QuestionType.QuestionGroup ||
    question.component === QuestionType.CreateAccount
  ) {
    return (
      <FieldArray name={questionName} key={`question-group-${questionName}`}>
        {() => {
          question =
            question.component === QuestionType.QuestionGroup
              ? (question as QuestionGroupType)
              : (question as QuestionCreateAccount)
          return (
            <QuestionGroup
              modifierClass={question.modifierClass}
              title={question.title}
              subTitle={question.subTitle}
              groupName={questionName}
              formValues={values}
              questions={question.questions}
            />
          )
        }}
      </FieldArray>
    )
  }

  if (question.component === QuestionType.RepeatableQuestionGroup) {
    // Need to wrap FieldArray so we can pass arrayHelpers into child component for top-level usage
    // Otherwise the RepeatableQuestionGroup component would not have access to the field array utilities.
    return (
      <FieldArray name={questionName} key={`repeatable-group-${questionName}`}>
        {(arrayHelpers) => {
          question = question as QuestionGroupType
          return (
            <RepeatableQuestionGroup
              title={question.title}
              subTitle={question.subTitle}
              groupName={questionName}
              formValues={values}
              questions={question.questions}
              titleForRepeatButton={question.titleForRepeatButton}
              textForRepeatButton={question.textForRepeatButton}
              textForDeleteButton={question.textForDeleteButton}
              autoRepeatValidationKey={question.autoRepeatValidationKey}
              fieldArrayHelpers={arrayHelpers}
            />
          )
        }}
      </FieldArray>
    )
  }

  if (question.component === QuestionType.AddressFieldsAutoComplete) {
    return (
      <AddressFieldsAutoComplete
        name={questionName}
        title={question.title}
        modifierClass={question.modifierClass}
        subTitle={question.subTitle}
        autoCompleteFillAddressValues={autoCompleteFillAddressValues}
        setValue={(value) => {
          fieldInputted({
            questionName,
            value,
            subTitle: question.subTitle,
          })
        }}
      />
    )
  }

  return <SingleQuestion question={question} key={questionName} />
}
